import { LoginWithOTPRequest, LoginWithPasswordRequest, OTPSendResponseDTO, PreLoginRequest, ResetPasswordRequest, ResetPasswordVerifyOtpRequest, ForgotPasswordEmailRequest } from "Interface/Client/Authentication/auth.interface";

import { ApiService } from "Services/ApiService";
import { ResultsDTO } from "Types/ResultsDTO";

export const AuthApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    register: builder.mutation<ResultsDTO, any>({
      query: (customerData) => ({
        url: "/auth/register",
        method: "POST",
        body: {
          ...customerData,
        },
      }),
    }),
    preRegister: builder.mutation<ResultsDTO, any>({
      query: (customerData) => ({
        url: "/auth/pre-register",
        method: "POST",
        body: {
          ...customerData,
        },
      }),
    }),
    loginWithPassword: builder.mutation<ResultsDTO, Partial<LoginWithPasswordRequest>>({
      query: (credentials) => ({
        url: "/auth/login-with-password",
        method: "POST",
        body: {
          ...credentials,
          loginType: "user",
        },
      }),
    }),
    loginWithOTP: builder.mutation<ResultsDTO, LoginWithOTPRequest>({
      query: (credentials) => ({
        url: "/auth/login-with-otp",
        method: "POST",
        body: {
          ...credentials
        },
      }),
    }),
    preOtpLogin: builder.mutation<ResultsDTO, PreLoginRequest>({
      query: (credentials) => ({
        url: "/auth/pre-otp-login",
        method: "POST",
        body: {
          ...credentials
        },
      })
    }),
    sendTwilioOtp: builder.mutation<ResultsDTO<OTPSendResponseDTO>, string>({
      query: (authenticationId: string) => ({
        url: `/auth/twilio/otp/send-otp?authenticationId=${authenticationId}`,
        method: 'POST'
      })
    }),
    registerSendTwilioOtp: builder.mutation<ResultsDTO<OTPSendResponseDTO>, string>({
      query: (mobileNo: string) => ({
        url: `/auth/twilio/otp/register-send-otp?mobileNo=${mobileNo}`,
        method: 'POST'
      }),
    }),
    sendFast2SmsOtp: builder.mutation<
      ResultsDTO<OTPSendResponseDTO>,
      string | {
        authenticationId: string;
        captchaId?: string | null;
        captchaInput?: string | null;
      }
    >({
      query: (arg) => {
        const authId = typeof arg === 'string' ? arg : arg.authenticationId;
        return {
          url: `/auth/fast2sms/otp/send-otp?authenticationId=${authId}`,
          method: 'POST',
          body: typeof arg === 'string' ? { authenticationId: arg } : arg,
          headers: {
            origin: window.location.origin,
          },
          responseHandler: (response) => response.text(),
        };
      },
      transformResponse: (response: any) => {
        try {
          return JSON.parse(response);
        } catch {
          return { message: response, otpStatus: "DELIVERED" };
        }
      },
      transformErrorResponse: (response) => {
        const parsed = typeof response.data === 'string'
          ? (() => { try { return JSON.parse(response.data); } catch { return { message: response.data }; } })()
          : response.data;
        return { ...(response as any), data: parsed };
      },
    }),

    registerSendFast2SmsOtp: builder.mutation<ResultsDTO<OTPSendResponseDTO>, string>({
      query: (mobileNo: string) => ({
        url: `/auth/fast2sms/otp/register-send-otp?mobileNo=${mobileNo}`,
        method: 'POST'
      })
    }),

    validateFast2SmsOtp: builder.mutation<ResultsDTO, { otp: string; username: string }>({
      query: (validateOTPDTO) => ({
        url: "/auth/fast2sms/otp/validate-otp",
        method: "POST",
        body: validateOTPDTO,
        headers: {
          origin: window.location.origin,
        },
      }),
    }),

    getCaptcha: builder.query<ResultsDTO, void>({
      query: () => ({
        url: "/auth/captcha",
        method: "GET",
      })
    }),

    forgotPasswordEmailOtp: builder.mutation<ResultsDTO, ForgotPasswordEmailRequest>({
      query: (request) => ({
        url: "/auth/forgot-pw-mail-otp",
        method: "POST",
        body: request,
      }),
      transformErrorResponse: (response) => {
        const parsed = typeof response.data === 'string'
          ? (() => { try { return JSON.parse(response.data); } catch { return { message: response.data }; } })()
          : response.data;
        return { ...(response as any), data: parsed };
      },
    }),
    resetPasswordVerifyOtp: builder.mutation<ResultsDTO, ResetPasswordVerifyOtpRequest>({
      query: (request) => ({
        url: "/auth/reset-password-verify-otp",
        method: "POST",
        body: request,
      }),
      transformErrorResponse: (response) => {
        const parsed = typeof response.data === 'string'
          ? (() => { try { return JSON.parse(response.data); } catch { return { message: response.data }; } })()
          : response.data;
        return { ...(response as any), data: parsed };
      },
    }),
    resetPassword: builder.mutation<ResultsDTO, ResetPasswordRequest>({
      query: (request) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: request,
      }),
      transformErrorResponse: (response) => {
        const parsed = typeof response.data === 'string'
          ? (() => { try { return JSON.parse(response.data); } catch { return { message: response.data }; } })()
          : response.data;
        return { ...(response as any), data: parsed };
      },
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginWithPasswordMutation,
  useLoginWithOTPMutation,
  usePreOtpLoginMutation,
  usePreRegisterMutation,
  useSendFast2SmsOtpMutation,
  useRegisterSendFast2SmsOtpMutation,
  useValidateFast2SmsOtpMutation,
  useForgotPasswordEmailOtpMutation,
  useResetPasswordVerifyOtpMutation,
  useResetPasswordMutation,
  useLazyGetCaptchaQuery
} = AuthApiSlice;
